import { useState } from "react";
import { LuArrowUpDown, LuChevronDown, LuChevronUp, LuBadgeCheck, LuBadgeX } from "react-icons/lu";

const INTERNAL_COLUMNS = [
  { key: "id", name: "ID", sort_value: "id", className: "w-[8%]" },
  { key: "user", name: "Përdoruesi", sort_value: "first_name", className: "w-[32%]" },
  { key: "phone", name: "Telefoni", sort_value: "phone_number", className: "w-[16%]" },
  { key: "role", name: "Roli", sort_value: "role", className: "w-[14%]" },
  { key: "status", name: "Statusi", className: "w-[12%]" },
  { key: "created", name: "Krijuar", sort_value: "created_at", className: "w-[14%]" },
];

/* Citizens all share one role, so show what identifies/qualifies them instead.
   Only fields the API can sort on get a sort_value. */
const CITIZEN_COLUMNS = [
  { key: "id", name: "ID", sort_value: "id", className: "w-[8%]" },
  { key: "user", name: "Qytetari", sort_value: "first_name", className: "w-[34%]" },
  { key: "phone", name: "Telefoni", sort_value: "phone_number", className: "w-[16%]" },
  { key: "municipality", name: "Komuna", className: "w-[16%]" },
  { key: "verified", name: "Verifikuar", className: "w-[12%]" },
  { key: "created", name: "Regjistruar", sort_value: "created_at", className: "w-[12%]" },
];

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const formatDate = (d) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("en-GB");
  } catch {
    return "-";
  }
};

const RolePill = ({ role }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200">
    {cap(role?.replace(/_/g, " ")) || "—"}
  </span>
);

/* Citizens are checked against the civil registry by a backend job:
   registry_verified 1 = matched, 0 = not found / name mismatch (login blocked), null = not checked yet */
export const REGISTRY_STATUS = {
  verified: { label: "Verifikuar", className: "text-sky-500", Icon: LuBadgeCheck },
  failed: { label: "Nuk u verifikua në regjistrin civil", className: "text-red-500", Icon: LuBadgeX },
  pending: { label: "Në pritje të verifikimit", className: "text-gray-300", Icon: LuBadgeCheck },
};

export const registryStatus = (user) => {
  if (user?.registry_verified === null || user?.registry_verified === undefined) return "pending";
  return Number(user.registry_verified) === 1 ? "verified" : "failed";
};

const VerifiedIcon = ({ user, size = 20 }) => {
  const { label, className, Icon } = REGISTRY_STATUS[registryStatus(user)];
  return (
    <span title={label} aria-label={label} role="img" className="inline-flex shrink-0">
      <Icon size={size} className={className} />
    </span>
  );
};

/* Staff accounts are created unverified and become verified once the emailed
   set-password link is used, so isVerified doubles as "account activated".
   Missing (e.g. token-only data) counts as active. */
export const isActiveUser = (user) =>
  user?.isVerified === undefined || user?.isVerified === null || Number(user.isVerified) === 1;

const StatusPill = ({ user }) =>
  isActiveUser(user) ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
      Aktiv
    </span>
  ) : (
    <span
      title="Përdoruesi ende nuk e ka vendosur fjalëkalimin nga linku në email"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
      Në pritje
    </span>
  );

const renderCell = (key, user) => {
  switch (key) {
    case "id":
      return <td key={key} className="px-6 py-3.5 text-gray-500 tabular-nums text-xs">#{user.id}</td>;
    case "user":
      return (
        <td key={key} className="px-6 py-3.5">
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate">
              {user.first_name} {user.last_name}
            </div>
            <div className="text-xs text-gray-500 truncate">{user.email || "—"}</div>
          </div>
        </td>
      );
    case "phone":
      return <td key={key} className="px-6 py-3.5 text-gray-700 whitespace-nowrap tabular-nums">{user.phone_number || "—"}</td>;
    case "municipality":
      return <td key={key} className="px-6 py-3.5 text-gray-700 truncate">{user.municipality_name || "—"}</td>;
    case "verified":
      return <td key={key} className="px-6 py-3.5"><VerifiedIcon user={user} /></td>;
    case "role":
      return <td key={key} className="px-6 py-3.5"><RolePill role={user.role} /></td>;
    case "status":
      return <td key={key} className="px-6 py-3.5"><StatusPill user={user} /></td>;
    case "created":
      return <td key={key} className="px-6 py-3.5 text-gray-600 whitespace-nowrap tabular-nums text-xs">{formatDate(user.created_at)}</td>;
    default:
      return <td key={key} />;
  }
};

const SkeletonRow = ({ columns }) => (
  <tr className="animate-pulse">
    {columns.map((col) => (
      <td key={col.key} className="px-6 py-4">
        {col.key === "user" ? (
          <div className="space-y-2">
            <div className="h-3 w-32 bg-gray-200 rounded" />
            <div className="h-2.5 w-48 bg-gray-100 rounded" />
          </div>
        ) : (
          <div className="h-3 w-16 bg-gray-200 rounded" />
        )}
      </td>
    ))}
  </tr>
);

const SortIcon = ({ active, order }) => {
  if (!active) return <LuArrowUpDown size={12} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />;
  return order === "desc" ? (
    <LuChevronDown size={14} className="text-[var(--secondary-color)]" />
  ) : (
    <LuChevronUp size={14} className="text-[var(--secondary-color)]" />
  );
};

export default function UserTable({
  users,
  isLoading,
  params = {},
  onSort,
  onRowClick,
  userType = "internal",
}) {
  const [hoverId, setHoverId] = useState(null);
  const isCitizen = userType === "citizen";
  const COLUMNS = isCitizen ? CITIZEN_COLUMNS : INTERNAL_COLUMNS;

  // Keep the skeleton height stable across page switches: match the number of
  // rows currently shown (falls back to a sensible default on first load).
  const skeletonCount = users?.length || 6;

  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {COLUMNS.map((col) => {
                const sortable = !!col.sort_value;
                const active = sortable && params.sort_by === col.sort_value;
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => onSort?.(col.sort_value) : undefined}
                    className={`group ${col.className} text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider ${
                      active ? "text-[var(--secondary-color)]" : "text-gray-500"
                    } ${sortable ? "cursor-pointer select-none hover:text-gray-900" : ""} transition-colors`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.name}
                      {sortable && <SortIcon active={active} order={params.sort_order} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              Array.from({ length: skeletonCount }).map((_, i) => <SkeletonRow key={i} columns={COLUMNS} />)
            ) : (
              users?.map((user) => (
                <tr
                  key={user.id}
                  onClick={() => onRowClick?.(user)}
                  onMouseEnter={() => setHoverId(user.id)}
                  onMouseLeave={() => setHoverId(null)}
                  className="group cursor-pointer transition-colors hover:bg-gray-50/70"
                >
                  {COLUMNS.map((col) => renderCell(col.key, user))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y divide-gray-100">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <div key={i} className="animate-pulse p-3 rounded-xl bg-gray-50 space-y-2">
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                <div className="h-2.5 w-3/4 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          users?.map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => onRowClick?.(user)}
              className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {user.first_name} {user.last_name}
                  </div>
                  <div className="text-xs text-gray-500">#{user.id}</div>
                </div>
                {isCitizen ? (
                  <VerifiedIcon user={user} />
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    <RolePill role={user.role} />
                    <StatusPill user={user} />
                  </div>
                )}
              </div>
              <div className="mt-1 text-xs text-gray-500 truncate">{user.email || "—"}</div>
              {isCitizen && (
                <div className="mt-1 text-xs text-gray-500 truncate">{user.municipality_name || "—"}</div>
              )}
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                <span>{user.phone_number || "—"}</span>
                <span className="tabular-nums">{formatDate(user.created_at)}</span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
